import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import * as Dialog from "@radix-ui/react-dialog";
import api from "../../services/api";
import MediaLibraryModal from "../../components/ui/MediaLibraryModal";

const SETTINGS_DEFAULTS = {
  bannerImages: [],
  bannerTitle: "New Clinic Setup",
  bannerSubtitle: "Everything you need to set up your clinic, in one place.",
};

const EMPTY_PACKAGE_FORM = {
  _id: null,
  name: "",
  type: "",
  price: "",
  priceNote: "",
  discountPercent: "",
  description: "",
  features: [""],
  specifications: [{ key: "", value: "" }],
  image: "",
  documents: [],
  isFeatured: false,
  status: "active",
  order: 0,
};

const SUGGESTED_TYPES = ["Basic", "Standard", "Premium", "Custom"];

const getFileKind = (type = "") => {
  if (type.includes("pdf")) return "pdf";
  if (type.includes("sheet") || type.includes("excel")) return "excel";
  if (type.includes("word") || type.includes("document")) return "word";
  return "file";
};

const KIND_LABEL = { pdf: "PDF", excel: "Excel", word: "Word", file: "File" };

const KIND_BADGE_CLASS = {
  pdf: "bg-red-50 text-red-600 border-red-200",
  excel: "bg-emerald-50 text-emerald-600 border-emerald-200",
  word: "bg-blue-50 text-blue-600 border-blue-200",
  file: "bg-admin-slate-100 text-admin-slate-600 border-admin-slate-200",
};

const TABS = [
  { key: "banner", label: "Hero Banner" },
  { key: "packages", label: "Setup Packages" },
];

const ClinicSetupPage = () => {
  const [activeTab, setActiveTab] = useState("banner");

  // ---- Page settings (hero banner) ----
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settings, setSettings] = useState(SETTINGS_DEFAULTS);
  const [isBannerLibraryOpen, setIsBannerLibraryOpen] = useState(false);

  // ---- Packages ----
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packageSaving, setPackageSaving] = useState(false);
  const [isPackageFormOpen, setIsPackageFormOpen] = useState(false);
  const [isPackageImageLibraryOpen, setIsPackageImageLibraryOpen] =
    useState(false);
  const [packageForm, setPackageForm] = useState(EMPTY_PACKAGE_FORM);
  const [deletingId, setDeletingId] = useState(null);
  const [packageDocUploading, setPackageDocUploading] = useState(false);
  const packageFileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    fetchPackages();
  }, []);

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await api.get("/admin/settings/clinic-setup");
      if (response.data.success) {
        setSettings({ ...SETTINGS_DEFAULTS, ...response.data.data });
      }
    } catch (error) {
      console.error("Error fetching clinic setup settings:", error);
      toast.error("Failed to load hero banner settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const response = await api.get("/clinic-setup-packages/admin/list");
      if (response.data.success) {
        setPackages(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching clinic setup packages:", error);
      toast.error("Failed to load setup packages");
    } finally {
      setPackagesLoading(false);
    }
  };

  // ---- Settings handlers ----
  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleBannerSelect = (selectedMedia) => {
    if (Array.isArray(selectedMedia) && selectedMedia.length > 0) {
      const newUrls = selectedMedia.map((media) => media.url);
      setSettings((prev) => ({
        ...prev,
        bannerImages: [...prev.bannerImages, ...newUrls],
      }));
    }
    setIsBannerLibraryOpen(false);
  };

  const handleRemoveBannerImage = (index) => {
    setSettings((prev) => ({
      ...prev,
      bannerImages: prev.bannerImages.filter((_, i) => i !== index),
    }));
  };

  const handleMoveBannerImage = (index, direction) => {
    setSettings((prev) => {
      const images = [...prev.bannerImages];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= images.length) return prev;
      [images[index], images[targetIndex]] = [
        images[targetIndex],
        images[index],
      ];
      return { ...prev, bannerImages: images };
    });
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setSettingsSaving(true);
      const response = await api.post("/admin/settings/clinic-setup", settings);
      if (response.data.success) {
        toast.success("Hero banner saved successfully");
      }
    } catch (error) {
      console.error("Error saving clinic setup settings:", error);
      toast.error(error.response?.data?.error || "Failed to save settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  // ---- Package handlers ----
  const existingTypes = useMemo(() => {
    const types = new Set(SUGGESTED_TYPES);
    packages.forEach((pkg) => pkg.type && types.add(pkg.type));
    return Array.from(types);
  }, [packages]);

  const openCreatePackageForm = () => {
    setPackageForm(EMPTY_PACKAGE_FORM);
    setIsPackageFormOpen(true);
  };

  const openEditPackageForm = (pkg) => {
    setPackageForm({
      _id: pkg._id,
      name: pkg.name || "",
      type: pkg.type || "",
      price: pkg.price ?? "",
      priceNote: pkg.priceNote || "",
      discountPercent: pkg.discountPercent || "",
      description: pkg.description || "",
      features: pkg.features?.length ? pkg.features : [""],
      specifications: pkg.specifications?.length
        ? pkg.specifications
        : [{ key: "", value: "" }],
      image: pkg.image || "",
      documents: pkg.documents?.length ? pkg.documents : [],
      isFeatured: !!pkg.isFeatured,
      status: pkg.status || "active",
      order: pkg.order || 0,
    });
    setIsPackageFormOpen(true);
  };

  const handlePackageFieldChange = (key, value) => {
    setPackageForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFeatureChange = (index, value) => {
    setPackageForm((prev) => ({
      ...prev,
      features: prev.features.map((f, i) => (i === index ? value : f)),
    }));
  };

  const handleAddFeature = () => {
    setPackageForm((prev) => ({ ...prev, features: [...prev.features, ""] }));
  };

  const handleRemoveFeature = (index) => {
    setPackageForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSpecChange = (index, field, value) => {
    setPackageForm((prev) => ({
      ...prev,
      specifications: prev.specifications.map((spec, i) =>
        i === index ? { ...spec, [field]: value } : spec,
      ),
    }));
  };

  const handleAddSpec = () => {
    setPackageForm((prev) => ({
      ...prev,
      specifications: [...prev.specifications, { key: "", value: "" }],
    }));
  };

  const handleRemoveSpec = (index) => {
    setPackageForm((prev) => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index),
    }));
  };

  const handlePackageImageSelect = (selectedMedia) => {
    if (Array.isArray(selectedMedia) && selectedMedia.length > 0) {
      handlePackageFieldChange("image", selectedMedia[0].url);
    }
    setIsPackageImageLibraryOpen(false);
  };

  const handlePackageDocUploadClick = () =>
    packageFileInputRef.current?.click();

  const handlePackageDocFilesChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    try {
      setPackageDocUploading(true);
      const response = await api.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        const newDocuments = response.data.data.map((media) => ({
          id: media._id,
          name: media.name,
          description: "",
          url: media.url,
          type: media.type,
        }));
        setPackageForm((prev) => ({
          ...prev,
          documents: [...prev.documents, ...newDocuments],
        }));
        toast.success(
          `${newDocuments.length} file${newDocuments.length > 1 ? "s" : ""} uploaded`,
        );
      }
    } catch (error) {
      console.error("Error uploading package document:", error);
      toast.error(error.response?.data?.error || "Failed to upload file");
    } finally {
      setPackageDocUploading(false);
      if (packageFileInputRef.current) packageFileInputRef.current.value = "";
    }
  };

  const handlePackageDocNameChange = (index, name) => {
    setPackageForm((prev) => ({
      ...prev,
      documents: prev.documents.map((doc, i) =>
        i === index ? { ...doc, name } : doc,
      ),
    }));
  };

  const handlePackageDocDescriptionChange = (index, description) => {
    setPackageForm((prev) => ({
      ...prev,
      documents: prev.documents.map((doc, i) =>
        i === index ? { ...doc, description } : doc,
      ),
    }));
  };

  const handleRemovePackageDoc = (index) => {
    setPackageForm((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  const handleSubmitPackage = async (e) => {
    e.preventDefault();

    if (
      !packageForm.name.trim() ||
      !packageForm.type.trim() ||
      packageForm.price === ""
    ) {
      toast.error("Name, type, and price are required");
      return;
    }

    const payload = {
      name: packageForm.name.trim(),
      type: packageForm.type.trim(),
      price: Number(packageForm.price),
      priceNote: packageForm.priceNote.trim(),
      discountPercent: packageForm.discountPercent === "" ? 0 : Number(packageForm.discountPercent),
      description: packageForm.description.trim(),
      features: packageForm.features.map((f) => f.trim()).filter(Boolean),
      specifications: packageForm.specifications
        .map((spec) => ({ key: spec.key.trim(), value: spec.value.trim() }))
        .filter((spec) => spec.key && spec.value),
      image: packageForm.image,
      documents: packageForm.documents,
      isFeatured: packageForm.isFeatured,
      status: packageForm.status,
      order: Number(packageForm.order) || 0,
    };

    try {
      setPackageSaving(true);
      if (packageForm._id) {
        await api.put(`/clinic-setup-packages/${packageForm._id}`, payload);
        toast.success("Package updated successfully");
      } else {
        await api.post("/clinic-setup-packages", payload);
        toast.success("Package created successfully");
      }
      setIsPackageFormOpen(false);
      fetchPackages();
    } catch (error) {
      console.error("Error saving clinic setup package:", error);
      toast.error(error.response?.data?.message || "Failed to save package");
    } finally {
      setPackageSaving(false);
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm("Delete this package? This cannot be undone.")) return;
    try {
      setDeletingId(id);
      await api.delete(`/clinic-setup-packages/${id}`);
      toast.success("Package deleted");
      setPackages((prev) => prev.filter((pkg) => pkg._id !== id));
    } catch (error) {
      console.error("Error deleting clinic setup package:", error);
      toast.error(error.response?.data?.message || "Failed to delete package");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">
          New Clinic Setup
        </h1>
        <p className="mt-1 text-sm text-admin-slate-600 dark:text-admin-slate-400"></p>
      </div>

      {/* Tabs */}
      <div className="border-b border-admin-slate-200 dark:border-admin-slate-700">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-admin-ucla-500 text-admin-ucla-600"
                  : "border-transparent text-admin-slate-500 hover:text-admin-slate-700 dark:hover:text-admin-slate-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Hero Banner tab */}
      {activeTab === "banner" &&
        (settingsLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSaveSettings} className="space-y-6">
            <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                  Hero Banner
                </h2>
                <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400 mt-1">
                  Add one or more images to show as a rotating carousel. With a
                  single image it displays as a static banner.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-3">
                    Carousel Images
                  </label>

                  {settings.bannerImages.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {settings.bannerImages.map((image, index) => (
                        <div
                          key={image + index}
                          className="flex items-center gap-3 border border-admin-slate-200 dark:border-admin-slate-700 rounded-lg p-2"
                        >
                          <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden bg-admin-slate-100">
                            <img
                              src={image}
                              alt={`Banner slide ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-xs text-admin-slate-500 flex-1">
                            Slide {index + 1}
                          </span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => handleMoveBannerImage(index, -1)}
                              disabled={index === 0}
                              className="p-1 text-admin-slate-500 hover:text-admin-ucla-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveBannerImage(index, 1)}
                              disabled={
                                index === settings.bannerImages.length - 1
                              }
                              className="p-1 text-admin-slate-500 hover:text-admin-ucla-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveBannerImage(index)}
                              className="p-1 text-red-600 hover:text-red-700 text-xs font-medium"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setIsBannerLibraryOpen(true)}
                    className="w-full border-2 border-dashed border-admin-slate-300 dark:border-admin-slate-600 rounded-lg py-4 flex flex-col items-center justify-center text-admin-slate-500 hover:border-admin-ucla-400 hover:text-admin-ucla-600 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 mb-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Add Slide{settings.bannerImages.length > 0 ? "s" : ""}
                    </span>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                      Banner Title
                    </label>
                    <input
                      type="text"
                      value={settings.bannerTitle}
                      onChange={(e) =>
                        handleSettingChange("bannerTitle", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-admin-ucla-500 bg-white dark:bg-admin-slate-900"
                      placeholder="New Clinic Setup"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                      Banner Subtitle
                    </label>
                    <textarea
                      value={settings.bannerSubtitle}
                      onChange={(e) =>
                        handleSettingChange("bannerSubtitle", e.target.value)
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-admin-ucla-500 bg-white dark:bg-admin-slate-900"
                      placeholder="Everything you need to set up your clinic, in one place."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={settingsSaving}
                className="bg-admin-ucla-500 hover:bg-admin-ucla-600 text-white font-medium py-2 px-6 rounded-md transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {settingsSaving ? "Saving..." : "Save Hero Banner"}
              </button>
            </div>
          </form>
        ))}

      {/* Packages tab */}
      {activeTab === "packages" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={openCreatePackageForm}
              className="bg-admin-ucla-500 hover:bg-admin-ucla-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              + Add Package
            </button>
          </div>

          <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg overflow-hidden">
            {packagesLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500"></div>
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-16 text-sm text-admin-slate-500 dark:text-admin-slate-400">
                No packages yet. Click "Add Package" to create your first one
                (e.g. Basic, Premium).
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
                  <thead className="bg-admin-slate-50 dark:bg-admin-slate-900/40">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-admin-slate-500 uppercase">
                        Package
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-admin-slate-500 uppercase">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-admin-slate-500 uppercase">
                        Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-admin-slate-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-admin-slate-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-admin-slate-100 dark:divide-admin-slate-700">
                    {packages.map((pkg) => (
                      <tr key={pkg._id}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {pkg.image ? (
                              <img
                                src={pkg.image}
                                alt={pkg.name}
                                className="w-10 h-10 rounded-md object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-md bg-admin-slate-100 dark:bg-admin-slate-700 flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-admin-slate-900 dark:text-admin-slate-100 truncate">
                                {pkg.name}
                                {pkg.isFeatured && (
                                  <span className="ml-2 text-2xs font-bold uppercase text-primary-700 bg-primary-100 rounded px-1.5 py-0.5">
                                    Featured
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-admin-slate-500 truncate max-w-xs">
                                {pkg.description}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-admin-slate-700 dark:text-admin-slate-300">
                          {pkg.type}
                        </td>
                        <td className="px-4 py-3 text-sm text-admin-slate-700 dark:text-admin-slate-300">
                          Rs. {Number(pkg.price).toLocaleString()}
                          {pkg.priceNote && (
                            <span className="text-admin-slate-400">
                              {" "}
                              {pkg.priceNote}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-2xs font-bold uppercase rounded px-2 py-1 ${
                              pkg.status === "active"
                                ? "bg-emerald-50 text-emerald-600"
                                : "bg-admin-slate-100 text-admin-slate-500"
                            }`}
                          >
                            {pkg.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => openEditPackageForm(pkg)}
                            className="text-admin-ucla-600 hover:text-admin-ucla-700 text-sm font-medium mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeletePackage(pkg._id)}
                            disabled={deletingId === pkg._id}
                            className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                          >
                            {deletingId === pkg._id ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <Dialog.Root
            open={isPackageFormOpen}
            onOpenChange={setIsPackageFormOpen}
          >
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
              <Dialog.Content className="fixed top-[5vh] left-1/2 -translate-x-1/2 w-[90vw] max-w-5xl max-h-[90vh] flex flex-col bg-white dark:bg-admin-slate-800 rounded-xl shadow-2xl z-50 overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-admin-slate-200 dark:border-admin-slate-700 shrink-0">
                  <Dialog.Title className="text-xl font-bold text-admin-slate-900 dark:text-admin-slate-100">
                    {packageForm._id ? "Edit Package" : "Add Package"}
                  </Dialog.Title>
                  <Dialog.Close className="text-admin-slate-400 hover:text-admin-slate-600 dark:hover:text-admin-slate-300 transition-colors">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Dialog.Close>
                </div>
                
                <div className="p-6 sm:p-8 overflow-y-auto">

                <form onSubmit={handleSubmitPackage} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                        Package Name *
                      </label>
                      <input
                        type="text"
                        value={packageForm.name}
                        onChange={(e) =>
                          handlePackageFieldChange("name", e.target.value)
                        }
                        placeholder="e.g. Basic Clinic Setup"
                        className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                        Type *
                      </label>
                      <input
                        type="text"
                        list="clinic-package-types"
                        value={packageForm.type}
                        onChange={(e) =>
                          handlePackageFieldChange("type", e.target.value)
                        }
                        placeholder="e.g. Basic, Premium, Custom"
                        className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                        required
                      />
                      <datalist id="clinic-package-types">
                        {existingTypes.map((t) => (
                          <option key={t} value={t} />
                        ))}
                      </datalist>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                        Price (Rs.) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={packageForm.price}
                        onChange={(e) =>
                          handlePackageFieldChange("price", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                        Price Note
                      </label>
                      <input
                        type="text"
                        value={packageForm.priceNote}
                        onChange={(e) =>
                          handlePackageFieldChange("priceNote", e.target.value)
                        }
                        placeholder="e.g. starting from, /month"
                        className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                        Discount %
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={packageForm.discountPercent}
                        onChange={(e) =>
                          handlePackageFieldChange("discountPercent", e.target.value)
                        }
                        placeholder="e.g. 10"
                        className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                      />
                      <p className="text-xs text-admin-slate-400 mt-1">
                        Leave blank for no discount badge
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={packageForm.description}
                      onChange={(e) =>
                        handlePackageFieldChange("description", e.target.value)
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                      placeholder="What this package covers"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                      Included Features
                    </label>
                    <div className="space-y-2">
                      {packageForm.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) =>
                              handleFeatureChange(index, e.target.value)
                            }
                            placeholder="e.g. Free installation"
                            className="flex-1 px-3 py-1.5 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(index)}
                            className="text-red-500 hover:text-red-600 text-sm px-1 flex-shrink-0"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddFeature}
                      className="mt-2 text-xs font-medium text-admin-ucla-600 hover:text-admin-ucla-700"
                    >
                      + Add Feature
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                      Specifications
                    </label>
                    <p className="text-xs text-admin-slate-400 mb-2">
                      Key/value details shown on the package's detail page (e.g.
                      Duration → 6 months, Warranty → 1 year).
                    </p>
                    <div className="space-y-2">
                      {packageForm.specifications.map((spec, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={spec.key}
                            onChange={(e) =>
                              handleSpecChange(index, "key", e.target.value)
                            }
                            placeholder="e.g. Duration"
                            className="w-1/3 px-3 py-1.5 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                          />
                          <input
                            type="text"
                            value={spec.value}
                            onChange={(e) =>
                              handleSpecChange(index, "value", e.target.value)
                            }
                            placeholder="e.g. 6 months"
                            className="flex-1 px-3 py-1.5 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveSpec(index)}
                            className="text-red-500 hover:text-red-600 text-sm px-1 flex-shrink-0"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddSpec}
                      className="mt-2 text-xs font-medium text-admin-ucla-600 hover:text-admin-ucla-700"
                    >
                      + Add Specification
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                      Image
                    </label>
                    <div className="flex items-center gap-3">
                      {packageForm.image ? (
                        <img
                          src={packageForm.image}
                          alt="Package preview"
                          className="w-16 h-16 rounded-md object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-md bg-admin-slate-100 dark:bg-admin-slate-700" />
                      )}
                      <button
                        type="button"
                        onClick={() => setIsPackageImageLibraryOpen(true)}
                        className="px-3 py-1.5 bg-admin-slate-100 dark:bg-admin-slate-700 hover:bg-admin-slate-200 dark:hover:bg-admin-slate-600 rounded-md text-sm"
                      >
                        {packageForm.image ? "Change Image" : "Select Image"}
                      </button>
                      {packageForm.image && (
                        <button
                          type="button"
                          onClick={() => handlePackageFieldChange("image", "")}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300">
                        Resources for this Package
                      </label>
                      <div>
                        <input
                          ref={packageFileInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          onChange={handlePackageDocFilesChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={handlePackageDocUploadClick}
                          disabled={packageDocUploading}
                          className="px-3 py-1.5 bg-admin-slate-100 dark:bg-admin-slate-700 hover:bg-admin-slate-200 dark:hover:bg-admin-slate-600 rounded-md text-xs font-medium disabled:opacity-50"
                        >
                          {packageDocUploading
                            ? "Uploading..."
                            : "+ Upload File"}
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400 mb-2">
                      Optional Excel, PDF, or Word files specific to this
                      package (e.g. a brochure or price list).
                    </p>

                    {packageForm.documents.length > 0 && (
                      <div className="space-y-2">
                        {packageForm.documents.map((doc, index) => {
                          const kind = getFileKind(doc.type);
                          return (
                            <div
                              key={doc.id || doc.url || index}
                              className="border border-admin-slate-200 dark:border-admin-slate-700 rounded-lg p-2.5 space-y-2"
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className={`flex-shrink-0 text-2xs font-bold uppercase border rounded px-1.5 py-0.5 ${KIND_BADGE_CLASS[kind]}`}
                                >
                                  {KIND_LABEL[kind]}
                                </span>
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-2xs text-admin-ucla-600 hover:underline flex-shrink-0"
                                >
                                  Preview
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleRemovePackageDoc(index)}
                                  className="ml-auto text-red-600 hover:text-red-700 text-xs flex-shrink-0"
                                >
                                  Remove
                                </button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  value={doc.name}
                                  onChange={(e) =>
                                    handlePackageDocNameChange(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  placeholder="File title"
                                  className="w-full px-2 py-1 border border-admin-slate-200 dark:border-admin-slate-700 rounded-md text-xs bg-white dark:bg-admin-slate-900"
                                />
                                <input
                                  type="text"
                                  value={doc.description || ""}
                                  onChange={(e) =>
                                    handlePackageDocDescriptionChange(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Optional description"
                                  className="w-full px-2 py-1 border border-admin-slate-200 dark:border-admin-slate-700 rounded-md text-xs bg-white dark:bg-admin-slate-900"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                        Display Order
                      </label>
                      <input
                        type="number"
                        value={packageForm.order}
                        onChange={(e) =>
                          handlePackageFieldChange("order", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">
                        Status
                      </label>
                      <select
                        value={packageForm.status}
                        onChange={(e) =>
                          handlePackageFieldChange("status", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-admin-slate-700 dark:text-admin-slate-300 pb-2">
                      <input
                        type="checkbox"
                        checked={packageForm.isFeatured}
                        onChange={(e) =>
                          handlePackageFieldChange(
                            "isFeatured",
                            e.target.checked,
                          )
                        }
                        className="rounded border-admin-slate-300"
                      />
                      Featured / Most Popular
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="px-4 py-2 text-sm font-medium text-admin-slate-600 hover:text-admin-slate-800"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="submit"
                      disabled={packageSaving}
                      className="bg-admin-ucla-500 hover:bg-admin-ucla-600 text-white font-medium py-2 px-6 rounded-md transition-colors disabled:opacity-50"
                    >
                      {packageSaving
                        ? "Saving..."
                        : packageForm._id
                          ? "Save Changes"
                          : "Create Package"}
                    </button>
                  </div>
                </form>
              </div>

                {/* Rendered inside Dialog.Content (not as a sibling of Dialog.Root) so
                    Radix doesn't mark it `inert` while the package form dialog is open -
                    an element outside a Radix Dialog's content is click-dead while the
                    dialog is open, even if it's visually on top. */}
                <MediaLibraryModal
                  isOpen={isPackageImageLibraryOpen}
                  onClose={() => setIsPackageImageLibraryOpen(false)}
                  onSelect={handlePackageImageSelect}
                  maxSelect={1}
                />
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      )}

      <MediaLibraryModal
        isOpen={isBannerLibraryOpen}
        onClose={() => setIsBannerLibraryOpen(false)}
        onSelect={handleBannerSelect}
        maxSelect={10}
      />
    </div>
  );
};

export default ClinicSetupPage;
